import * as _ from 'lodash';

import { TagDefinition, SearchFormat } from '../../models';
import { ILoggerService } from '../../services/general';
import { Helper } from '../../common';

export interface ITagsFilteringService {
    filterResults(items: any[], filterString: string, groupSeparator: string): TagDefinition[];
    filterResults<T>(items: any[], filterString: string, groupSeparator: string, format: SearchFormat<T>): T[];
    getActiveFilter(context?: string): string;
    setActiveFilter(search: string, context?: string): void;
}

export class TagsFilteringService implements ITagsFilteringService {

    static serviceId = 'tagsFilteringService';

    searches: string[] = [];
    private format: any;

    static $inject = ['loggerService'];
    constructor(private loggerService: ILoggerService) {
    }

    filterResults(items: any[], filterString: string, groupSeparator: string): TagDefinition[];
    filterResults<T>(items: any[], filterString: string, groupSeparator: string, format: SearchFormat<T>): T[];
    filterResults<T>(items: any[], filterString: string, groupSeparator: string, format?: SearchFormat<T>): T[] {
        let filteredItems: any[] = [];

        if (items) {
            items.forEach(svm => {
                filteredItems.push(svm);
            });
        }

        if (filterString) {
            const patterns = filterString.split(' ');

            let nameFunction = f => f.name;
            let tagFunction = f => {
                return (f.tagName ? f.tagName : (f.name ? f.name : ''));
            };
            let commentFunction = f => f.comment ? f.comment : f.shortComment;
            let variableFunction = f => f.variableTypeName ? f.variableTypeName : f.variableName;
            let unitFunction = f => f.unitName ? f.unitName : '';
            let sourceFunction = f => f.source ? f.source : (f.connectionPointName ? f.connectionPointName : '');
            let errorFunction = f => f.error;
            let severityFunction = f => f.level;
            const messageFunction = f => f.message;
            const applicationFunction = f => f.application;
            let groupFunction = f => {
                return f.groupList && f.groupList.length > 0 ? _.map(f.groupList, 'absoluteName').join(',') : '';
            };
            const identifierFunction = f => f.identifierNames ? f.identifierNames.join(',') : '';
            let queryFunction = f => f.name;


            const customFunctions = [];
            if (!format) {
                format = this.format;
            }
            if (format) {
                if (format.name) {
                    nameFunction = format.name;
                }
                if (format.tag) {
                    tagFunction = format.tag;
                }
                if (format.comment) {
                    commentFunction = format.comment;
                }
                if (format.variable) {
                    variableFunction = format.variable;
                }
                if (format.unit) {
                    unitFunction = format.unit;
                }
                if (format.source) {
                    sourceFunction = format.source;
                }
                if (format.error) {
                    errorFunction = format.error;
                }
                if (format.group) {
                    groupFunction = format.group;
                }
                if (format.severity) {
                    severityFunction = format.severity;
                }
                if (format.query) {
                    queryFunction = format.query;
                }
                // implement custom search function
                for (const func of Object.keys(format)) {
                    if (format.hasOwnProperty(func) &&
                        !_.includes(['name', 'comment', 'variable', 'unit', 'source', 'error', 'group'], func)) {
                        customFunctions[func] = format[func];
                    }
                }
            }

            patterns.forEach(p => {
                try {
                    let isCaseInsensitive: boolean = true;
                    const criteria = p.split(':');
                    if (criteria && criteria.length > 0) {
                        p = p.toLowerCase();
                        criteria.forEach((value, index) => criteria[index] = value.toLowerCase());
                        isCaseInsensitive = true;
                    }
                    let regExp: RegExp;
                    if (criteria.length === 1) {
                        regExp = Helper.getRegExpCase(criteria[0], isCaseInsensitive);


                        const functions = [
                            tagFunction, commentFunction, variableFunction, unitFunction, sourceFunction,
                            errorFunction, severityFunction, messageFunction, applicationFunction, groupFunction,
                            identifierFunction, nameFunction, queryFunction
                        ];
                        const evalFunction = f => functions.map(d => d(f)).join(';;;;;;;;;;;');
                        this.remove(filteredItems, regExp, evalFunction);
                    } else if (criteria.length === 2) {
                        regExp = Helper.getRegExpCase(criteria[1], isCaseInsensitive);
                        switch (criteria[0]) {
                            case 'g':
                            case 'group':
                                if (groupFunction) {
                                    this.remove(filteredItems, regExp, groupFunction);
                                } else {
                                    _.remove(filteredItems, f => nameFunction(f).toLowerCase().indexOf(groupSeparator) === -1);
                                    this.remove(filteredItems, regExp, f => nameFunction(f).split(groupSeparator)[0]);
                                }
                                break;
                            case 'i':
                            case 'identifier':
                                this.remove(filteredItems, regExp, f => identifierFunction(f));
                                break;
                            case 't':
                            case 'tag':
                                this.remove(filteredItems, regExp, tagFunction, isCaseInsensitive);
                                break;
                            case 'v':
                            case 'variable':
                                this.remove(filteredItems, regExp, variableFunction);
                                break;
                            case 'u':
                            case 'unit':
                                this.remove(filteredItems, regExp, unitFunction);
                                break;
                            case 'c':
                            case 'comment':
                                this.remove(filteredItems, regExp, commentFunction);
                                break;
                            case 'so':
                            case 'source':
                                this.remove(filteredItems, regExp, sourceFunction);
                                break;
                            case 'err':
                            case 'error':
                                this.remove(filteredItems, regExp, errorFunction);
                                if (format && format.order) {
                                    filteredItems = format.order(regExp, filteredItems);
                                }
                                break;
                            case 's':
                            case 'severity':
                                this.remove(filteredItems, regExp, severityFunction);
                                break;
                            case 'm':
                            case 'message':
                                this.remove(filteredItems, regExp, messageFunction);
                                break;
                            case 'a':
                            case 'application':
                                this.remove(filteredItems, regExp, applicationFunction);
                                break;
                            case 'q':
                                this.remove(filteredItems, regExp, queryFunction);
                                break;
                            case 'gt':
                                if (customFunctions['gt']) {
                                    this.remove(filteredItems, regExp, customFunctions['gt'], true);
                                }
                                break;
                            default:
                                if (customFunctions.hasOwnProperty(criteria[0])) {
                                    this.remove(filteredItems, regExp, customFunctions[criteria[0]]);
                                }
                                break;
                        }
                    }
                } catch (e) {
                    this.loggerService.logError(e);
                }
            });
        }

        return filteredItems;
    }

    private remove(
        filteredItems: any[],
        regExp: RegExp,
        method: (f: any) => any,
        testEmpty: boolean = false
    ): void {
        _.remove(filteredItems, f => {
            let val = method(f);
            const isEmpty = (val === undefined || val === null);
            val = val?.toLowerCase();
            return (!isEmpty || testEmpty)
                ? !this.match(regExp, val ?? '')
                : true;
        });
    }

    private match(regExp: RegExp, value: string): boolean {
        return regExp
            ? regExp.test(value)
            : false;
    }

    getActiveFilter(context?: string): string {
        //return (this.searches['default'] === undefined) ? undefined : this.searches['default'];
        return this.searches[context ? context : 'default'];
    }

    setActiveFilter(search: string, context?: string): void {
        this.searches[context ? context : 'default'] = search;
    }
}